import { mimeTypes } from '@picsio/db/src/fileTypes';

import {
  SHOPIFY_NATIVE_EXTENSIONS,
  SHOPIFY_PREPARE_THUMBNAIL_EXTENSIONS,
} from './needsShopifyPrepareFormat';

/** Media types Shopify accepts as native uploads. */
const SHOPIFY_SUPPORTED_TYPES = ['IMAGE', 'VIDEO', 'MODEL_3D'];

/** Shopify upload size limits (bytes) — mirrors shopify-toriut invalidFileSize. */
const SHOPIFY_MAX_FILE_SIZE = {
  IMAGE: 20971520,
  VIDEO: 1073741824,
  MODEL_3D: 536870912,
};

export const PUBLISH_BLOCK_REASONS = {
  THUMBNAIL_PROCESSING: 'thumbnail_processing',
  THUMBNAIL_FAILED: 'thumbnail_failed',
  THUMBNAIL_MISSING: 'thumbnail_missing',
  UNSUPPORTED_MEDIA_TYPE: 'unsupported_media_type',
};

/** Tooltip / UI priority when multiple assets block publish. */
export const PUBLISH_BLOCK_REASON_PRIORITY = [
  PUBLISH_BLOCK_REASONS.THUMBNAIL_PROCESSING,
  PUBLISH_BLOCK_REASONS.THUMBNAIL_FAILED,
  PUBLISH_BLOCK_REASONS.THUMBNAIL_MISSING,
  PUBLISH_BLOCK_REASONS.UNSUPPORTED_MEDIA_TYPE,
];

/**
 * Resolves how a single attached asset should be published to Shopify.
 * FE mirror of server resolvePublishMedia (PCS-11014). Server remains hard gate.
 *
 * @param {Object} asset
 * @returns {{
 *   canPublish: boolean,
 *   mediaType: ('IMAGE'|'VIDEO'|'MODEL_3D'|null),
 *   useThumbnail: boolean,
 *   thumbnailStatus: string,
 *   reason: (string|null)
 * }}
 */
export default function resolvePublishMedia(asset = {}) {
  const mediaType = detectShopifyMediaType(asset);
  const isSupportedType = SHOPIFY_SUPPORTED_TYPES.includes(mediaType);
  const isOversize = isSupportedType && isInvalidFileSize(asset.fileSize, mediaType);

  if (isSupportedType && !isOversize) {
    return {
      canPublish: true,
      mediaType,
      useThumbnail: false,
      thumbnailStatus: 'not_required',
      reason: null,
    };
  }

  if (isImageClassAsset(asset)) {
    const { thumbnailStatus, reason } = resolveThumbnailState(asset);

    if (thumbnailStatus === 'ready') {
      return {
        canPublish: true,
        mediaType: 'IMAGE',
        useThumbnail: true,
        thumbnailStatus,
        reason: null,
      };
    }

    return {
      canPublish: false,
      mediaType: 'IMAGE',
      useThumbnail: false,
      thumbnailStatus,
      reason,
    };
  }

  return {
    canPublish: false,
    mediaType,
    useThumbnail: false,
    thumbnailStatus: 'not_required',
    reason: PUBLISH_BLOCK_REASONS.UNSUPPORTED_MEDIA_TYPE,
  };
}

/**
 * Aggregates publish readiness for a list of listing/variant assets.
 *
 * @param {Object[]} [assets]
 * @returns {{
 *   canPublish: boolean,
 *   reason: (string|null),
 *   processingCount: number,
 *   blocking: Array
 * }}
 */
export function aggregatePublishMediaReadiness(assets = []) {
  const blocking = [];
  let processingCount = 0;

  (assets || []).forEach((asset) => {
    if (!asset) return;
    const resolution = resolvePublishMedia(asset);
    if (!resolution.canPublish) {
      blocking.push({
        assetId: asset._id ? String(asset._id) : null,
        ...resolution,
      });
      if (resolution.reason === PUBLISH_BLOCK_REASONS.THUMBNAIL_PROCESSING) {
        processingCount += 1;
      }
    }
  });

  if (!blocking.length) {
    return {
      canPublish: true,
      reason: null,
      processingCount: 0,
      blocking: [],
    };
  }

  const reason = PUBLISH_BLOCK_REASON_PRIORITY.find((candidate) => (
    blocking.some((entry) => entry.reason === candidate)
  )) || blocking[0].reason;

  return {
    canPublish: false,
    reason,
    processingCount,
    blocking,
  };
}

function detectShopifyMediaType(asset) {
  return SHOPIFY_NATIVE_EXTENSIONS[getAssetExtension(asset)] || null;
}

function isInvalidFileSize(fileSize, mediaType) {
  if (!mediaType) return true;
  const size = parseInt(fileSize, 10);
  const max = SHOPIFY_MAX_FILE_SIZE[mediaType];
  if (!max) return true;
  // NaN comparisons are false — matches shopify-toriut invalidFileSize.
  return size > max;
}

function isImageClassAsset(asset) {
  const mimeType = asset.mimeType || '';
  if (mimeType.startsWith('image/')) return true;
  if ((mimeTypes.raw || []).includes(mimeType) || (mimeTypes.psd || []).includes(mimeType)) {
    return true;
  }
  return SHOPIFY_PREPARE_THUMBNAIL_EXTENSIONS.has(getAssetExtension(asset));
}

function getAssetExtension(asset) {
  if (asset.fileExtension) {
    return String(asset.fileExtension).toLowerCase().replace(/^\./, '');
  }
  const name = asset.name || '';
  const dot = name.lastIndexOf('.');
  if (dot === -1) return '';
  return name.slice(dot + 1).toLowerCase();
}

function resolveThumbnailState(asset) {
  const hasThumbnailFile = Boolean(asset.customThumbnail && asset.customThumbnail.head);
  const { thumbnailing } = asset;

  if (thumbnailing === 'complete' && hasThumbnailFile) {
    return { thumbnailStatus: 'ready', reason: null };
  }
  if (thumbnailing === 'waiting' || thumbnailing === 'delayed') {
    return {
      thumbnailStatus: 'waiting',
      reason: PUBLISH_BLOCK_REASONS.THUMBNAIL_PROCESSING,
    };
  }
  if (thumbnailing === 'running') {
    return {
      thumbnailStatus: 'running',
      reason: PUBLISH_BLOCK_REASONS.THUMBNAIL_PROCESSING,
    };
  }
  if (thumbnailing === 'failed' || thumbnailing === 'rejected') {
    return {
      thumbnailStatus: 'failed',
      reason: PUBLISH_BLOCK_REASONS.THUMBNAIL_FAILED,
    };
  }

  return {
    thumbnailStatus: 'missing',
    reason: PUBLISH_BLOCK_REASONS.THUMBNAIL_MISSING,
  };
}
